import { useEffect, useState, type ReactNode } from "react";
import { Loader2, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { SavedViewRead } from "@/api/types";

interface SaveAsNewViewModalProps {
  canShare: boolean;
  onSave: (data: {
    name: string;
    is_shared: boolean;
  }) => Promise<SavedViewRead>;
  /** Pre-fills the name input. Used by "Save as new" from a shared
   *  view to suggest e.g. "Hot Prospects (copy)". */
  defaultName?: string;
  /** Trigger button label. Defaults to "Save view". */
  triggerLabel?: string;
  /** Trigger button icon. Defaults to a Plus glyph. */
  triggerIcon?: ReactNode;
  /** Extra classes merged onto the trigger button — used by the
   *  "All" tab split-button to join the caret to the primary action.
   *  Omitted everywhere else, so existing call sites are unaffected. */
  triggerClassName?: string;
  /** Optional preview rendering: one human-readable line per
   *  active filter. When provided, the modal shows a "Preview"
   *  section enumerating exactly what the new view will persist.
   *  Empty array (provided but length=0) renders the
   *  "No filters applied — saves the empty / 'All' state"
   *  message. Undefined means "preview not applicable on this
   *  page" — section is hidden entirely. */
  previewItems?: string[];
  /** Optional sort preview — F3-bis-frontend. When non-empty,
   *  rendered as a "Sort: …" line ABOVE the filter chip lines so
   *  the user can verify the sort order alongside the filters.
   *  Empty / undefined → no sort line, only the filter list. */
  sortPreview?: string;
  /** Optional controlled open state — when provided the modal is
   *  driven by the parent and the internal trigger button is
   *  hidden (the parent provides its own trigger). Used by the F6
   *  ViewsDropdown's "+ Create new view" button so the dropdown
   *  closes before the modal opens, avoiding stacked popovers. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Modal flow for creating a new saved view from the current
 *  filter state. F3 replacement for the F1/F2-era SaveViewPopover —
 *  same callback contract, centered Dialog instead of an inline
 *  Popover, optional preview block that lets the user verify what
 *  they're about to persist. */
export function SaveAsNewViewModal({
  canShare,
  onSave,
  defaultName,
  triggerLabel = "Save view",
  triggerIcon,
  triggerClassName,
  previewItems,
  sortPreview,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: SaveAsNewViewModalProps) {
  // Either-controlled-or-uncontrolled pattern. When the parent
  // supplies `open` + `onOpenChange` we defer to it; otherwise
  // fall back to local state (the legacy direct-trigger usage).
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };
  const [name, setName] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset form fields whenever the modal opens. We don't reset on
  // close — keeping the typed name + share toggle between cancel
  // and re-open inside the same session would be one less paper
  // cut, but the rest of the codebase (and the legacy popover)
  // explicitly resets, so we mirror that for behavioural parity.
  useEffect(() => {
    if (open) {
      setName(defaultName ?? "");
      setIsShared(false);
      setSaving(false);
    }
  }, [open, defaultName]);

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSave({ name: trimmed, is_shared: isShared });
      setOpen(false);
    } catch {
      // The parent's onSave wrapper raises its own toast; we just
      // surface the saving flag back to false so the user can retry
      // from a still-open modal (per D12: error keeps the modal up).
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (saving) return;
        setOpen(o);
      }}
    >
      {/* Internal trigger button is only rendered in the legacy
       *  uncontrolled mode. When the parent supplies `open` +
       *  `onOpenChange` (F6 ViewsDropdown), the parent renders
       *  its own trigger and we just host the Dialog content. */}
      {!isControlled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className={cn("shrink-0 border-dashed", triggerClassName)}
        >
          {triggerIcon ?? <Plus className="mr-1.5 h-3.5 w-3.5" />}
          {triggerLabel}
        </Button>
      )}
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Save as new view</DialogTitle>
          <DialogDescription>
            This will create a new saved view from your current filters.
          </DialogDescription>
        </DialogHeader>

        {(previewItems !== undefined ||
          (sortPreview && sortPreview.length > 0)) && (
          <div className="space-y-1.5">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Preview
            </div>
            {previewItems !== undefined &&
            previewItems.length === 0 &&
            (!sortPreview || sortPreview.length === 0) ? (
              <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs italic text-slate-500">
                No filters applied, saves the empty / “All” state.
              </p>
            ) : (
              <ul className="space-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                {/* Sort line — shown above the filter chip lines per
                 *  F3-bis-frontend D11. Single line because sorts
                 *  are joined into one comma-separated string by
                 *  getSortDisplayString. */}
                {sortPreview && sortPreview.length > 0 && (
                  <li
                    key="__sort__"
                    className="truncate text-xs text-slate-700"
                    title={`Sort: ${sortPreview}`}
                  >
                    <span className="font-medium">Sort:</span> {sortPreview}
                  </li>
                )}
                {(previewItems ?? []).map((line, i) => (
                  <li
                    key={`${line}-${i}`}
                    className="truncate text-xs text-slate-700"
                    title={line}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            View name
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="e.g. Hot Prospects"
            maxLength={100}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSave();
            }}
            className="h-9 rounded-lg border-slate-200"
          />
        </div>

        {canShare && (
          <label className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">
                Share with team
              </div>
              <div className="text-xs text-slate-500">
                Everyone in this project can see and use this view.
              </div>
            </div>
            <Switch
              checked={isShared}
              onCheckedChange={setIsShared}
            />
          </label>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleSave()}
            disabled={saving || name.trim() === ""}
          >
            {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
